import { useInterval } from 'hooks/usePolling';
import { useStore } from 'hooks/useStore';
import { SetupTime, SetupTimePayload } from 'Interfaces/ReferenceInterfaces/SetupTimeInterface';
import { DataTableStateEvent } from 'primereact/datatable';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { getJitteredInterval } from 'utils/pollingUtility';
import * as XLSX from 'xlsx';

interface FormData {
  roomTypeId: number;
  bottleCountMin: number;
  bottleCountMax: number;
  incomingJobTypeId: number;
  isIncomingJobAndOutgoingJobBottleStockCodeSame: boolean;
  isIncomingJobAndOutgoingJobCapStockCodeSame: boolean;
  cleanTypeId: number;
  setupTimeMinutes: number;
}

export const useSetupTimeTable = (pageSize: number = 100) => {
  const { setupTimeStore, roomStore, cleanTypeStore, runTypeStore } = useStore();

  const [selectedRow, setSelectedRow] = useState<SetupTime | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const itemMenu = useRef<any>(null);
  const environment = import.meta.env.VITE_ENVIRONMENT;
  const POLLING_INTERVAL = getJitteredInterval();

  // Load initial data when component mounts
  useEffect(() => {
    setupTimeStore.fetchSetupTimes(currentPage, pageSize);
    roomStore.fetchRooms();
    roomStore.fetchRoomTypes();
    cleanTypeStore.fetchCleanTypes();
    runTypeStore.fetchRunTypes();
  }, [currentPage, pageSize]);

  // Set up polling for data updates
  useInterval(async () => {
    try {
      await setupTimeStore.pollSetupTimes(currentPage, pageSize);
      await roomStore.pollRoomTypes();
    } catch (error) {
      // Don't break the polling cycle on error
      if (environment === 'development') {
        console.error('Error during polling:', error);
      }
      toast.error('Error during polling for Setup Time data!');
    }
  }, POLLING_INTERVAL);

  // Handle page change
  const onPageChange = (event: DataTableStateEvent) => {
    if (event.page !== undefined) {
      const newPage = event.page + 1;
      setCurrentPage(newPage);
      setupTimeStore.fetchSetupTimes(newPage, 100);
    }
  };

  // Prepare room options from roomStore
  const roomOptions = roomStore.rooms.map(room => ({
    label: room.room_short ? `${room.room_short} - ${room.room_long}` : room.room_long || '',
    value: room.id
  }));

  // Prepare room type options from roomStore room types
  const roomTypeOptions = roomStore.roomTypeOptions;

  // Prepare clean type options from cleanTypeStore
  const cleanTypeOptions = cleanTypeStore.cleanTypes.map(cleanType => ({
    label: cleanType.description || '',
    value: cleanType.id
  }));

  // Prepare incoming job type options from runTypeStore
  const incomingJobTypeOptions = runTypeStore.runTypes.map(runType => ({
    label: runType.description || '',
    value: runType.id
  }));

  const handleAddRow = async (formData: FormData): Promise<void> => {
    if (selectedRow) {
      const updatePayload: SetupTimePayload = {
        Room_Type: formData.roomTypeId,
        bottle_count_min: formData.bottleCountMin,
        bottle_count_max: formData.bottleCountMax,
        incoming_job_type: formData.incomingJobTypeId,
        is_incoming_job_and_outgoing_job_bottle_stock_code_same:
          formData.isIncomingJobAndOutgoingJobBottleStockCodeSame,
        is_incoming_job_and_outgoing_job_cap_stock_code_same:
          formData.isIncomingJobAndOutgoingJobCapStockCodeSame,
        CleanType: formData.cleanTypeId,
        setup_time_minutes: formData.setupTimeMinutes
      };

      const success = await setupTimeStore.updateSetupTime(selectedRow.id, updatePayload);
      if (!success) {
        throw new Error('Failed to update setup time');
      }
    } else {
      const createPayload: SetupTimePayload = {
        Room_Type: formData.roomTypeId,
        bottle_count_min: formData.bottleCountMin,
        bottle_count_max: formData.bottleCountMax,
        incoming_job_type: formData.incomingJobTypeId,
        is_incoming_job_and_outgoing_job_bottle_stock_code_same:
          formData.isIncomingJobAndOutgoingJobBottleStockCodeSame,
        is_incoming_job_and_outgoing_job_cap_stock_code_same:
          formData.isIncomingJobAndOutgoingJobCapStockCodeSame,
        CleanType: formData.cleanTypeId,
        setup_time_minutes: formData.setupTimeMinutes,
        flag: true
      };

      const success = await setupTimeStore.createSetupTime(createPayload);
      if (!success) {
        throw new Error('Failed to create setup time');
      }
    }
    // Store methods already fetch updated data - no need to fetch again
    // Don't clear selectedRow here - let the modal's onHide handle cleanup
  };

  const handleDeleteRow = async () => {
    if (selectedRow) {
      await setupTimeStore.deleteSetupTime(selectedRow.id);
      setSelectedRow(null);
      setShowDeleteModal(false);
      await setupTimeStore.fetchSetupTimes(currentPage, pageSize);
    }
  };

  const prepareFormData = (row: SetupTime): FormData => {
    return {
      roomTypeId: row.Room_Type.id || 0,
      bottleCountMin: row.bottle_count_min || 0,
      bottleCountMax: row.bottle_count_max || 0,
      incomingJobTypeId: row.incoming_job_type.id || 0,
      isIncomingJobAndOutgoingJobBottleStockCodeSame:
        row.is_incoming_job_and_outgoing_job_bottle_stock_code_same || false,
      isIncomingJobAndOutgoingJobCapStockCodeSame:
        row.is_incoming_job_and_outgoing_job_cap_stock_code_same || false,
      cleanTypeId: row.CleanType.id || 0,
      setupTimeMinutes: row.setup_time_minutes || 0
    };
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  const filteredSetupTimeData = setupTimeStore.setupTimes.filter(item => {
    if (!searchQuery.trim()) {
      return true;
    }

    const searchLower = searchQuery.toLowerCase();
    return (
      item.Room_Type.description.toLowerCase().includes(searchLower) ||
      item.incoming_job_type.description.toLowerCase().includes(searchLower) ||
      item.CleanType.description.toLowerCase().includes(searchLower) ||
      item.bottle_count_min.toString().includes(searchQuery) ||
      item.bottle_count_max.toString().includes(searchQuery) ||
      item.setup_time_minutes.toString().includes(searchQuery) ||
      (item.is_incoming_job_and_outgoing_job_bottle_stock_code_same ? 'yes' : 'no').includes(
        searchLower
      ) ||
      (item.is_incoming_job_and_outgoing_job_cap_stock_code_same ? 'yes' : 'no').includes(
        searchLower
      )
    );
  });

  // Helper function to convert boolean/null to display string
  const getBooleanDisplayValue = (value: boolean | null): string => {
    if (value === null) {
      return 'N/A';
    }

    return value ? 'Yes' : 'No';
  };

  const handleExportToExcel = () => {
    const exportData = filteredSetupTimeData.map(item => ({
      'Room Type': item.Room_Type.description,
      'Bottle Count Min': item.bottle_count_min,
      'Bottle Count Max': item.bottle_count_max,
      'Incoming Job Type': item.incoming_job_type.description,
      'Same Bottle Stock Code': getBooleanDisplayValue(
        item.is_incoming_job_and_outgoing_job_bottle_stock_code_same
      ),
      'Same Cap Stock Code': getBooleanDisplayValue(
        item.is_incoming_job_and_outgoing_job_cap_stock_code_same
      ),
      'Clean Type': item.CleanType.description,
      'Setup Time (mins)': item.setup_time_minutes
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Setup Times');
    XLSX.writeFile(workbook, 'setup_times_export.xlsx');
  };

  return {
    setupTimeData: filteredSetupTimeData,
    isLoading: setupTimeStore.isLoading || roomStore.roomTypesLoading,
    pagination: setupTimeStore.pagination,
    itemMenu,
    selectedRow,
    showAddModal,
    showDeleteModal,
    setSelectedRow,
    setShowAddModal,
    setShowDeleteModal,
    handleAddRow,
    handleDeleteRow,
    prepareFormData,
    roomOptions,
    cleanTypeOptions,
    incomingJobTypeOptions,
    onPageChange,
    currentPage,
    roomTypeOptions,
    searchQuery,
    handleSearchChange,
    handleExportToExcel
  };
};
